/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import GenericModal from "components/Modal"; // Adjust path if needed
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { Domain } from "../sharedUtils";
import { Trash2, Edit2, Plus, RefreshCw } from "lucide-react";

interface Props {
    isOpen: boolean;
    onClose: () => void;
}

const defaultFormState = {
    id: 0,
    domain: "",
    role: "",
    focusArea: "",
    itemHints: "",
    extraRules: ""
};

export default function ManageDomainsModal({ isOpen, onClose }: Props) {
    const { addToast } = useToaster();
    const [domains, setDomains] = useState<Domain[]>([]);
    const [loading, setLoading] = useState(false);
    const [view, setView] = useState<"LIST" | "FORM">("LIST");
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);

    // Form State
    const [formData, setFormData] = useState(defaultFormState);
    const [initialFormData, setInitialFormData] = useState(defaultFormState); // Tracks initial state for dirty check
    const [saving, setSaving] = useState(false);

    const fetchDomains = async () => {
        setLoading(true);
        try {
            const res = await apiService.get("api/gp/domains");
            setDomains(res.data || []);
        } catch (e) {
            addToast("Failed to load domains.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setView("LIST");
            fetchDomains();
        }
    }, [isOpen]);

    const handleEdit = (d: Domain) => {
        const editState = {
            id: d.id,
            domain: d.domain || "",
            role: d.role || "",
            focusArea: d.focusArea || "",
            // Join array with newlines for the textarea
            itemHints: Array.isArray(d.itemHints) ? d.itemHints.join("\n") : "",
            extraRules: d.extraRules || ""
        };
        setFormData(editState);
        setInitialFormData(editState); // Save initial state
        setView("FORM");
    };

    const handleDelete = (id: number) => {
        setSelectedDomainId(id);
        setOpenDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!selectedDomainId || deleting) return;

        setDeleting(true);

        try {
            await apiService.delete(`api/gp/domains/${selectedDomainId}`);

            addToast("Domain deleted successfully.", "success");

            setOpenDeleteModal(false);
            setSelectedDomainId(null);

            fetchDomains();
        } catch (e: any) {
            addToast(
                e?.response?.data?.error || "Failed to delete domain.",
                "error"
            );
        } finally {
            setDeleting(false);
        }
    };

    const handleAddDomainClick = () => {
        setFormData(defaultFormState);
        setInitialFormData(defaultFormState); // Save initial state
        setView("FORM");
    };

    // Helper to check if form is fully valid
    const isFormValid =
        formData.domain.trim() &&
        formData.role.trim() &&
        formData.focusArea.trim() &&
        formData.itemHints.trim() &&
        formData.extraRules.trim();

    // Helper to check if form is modified
    const isFormDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isFormValid || !isFormDirty) return;

        try {
            setSaving(true);

            const hintsArray = formData.itemHints
                .split('\n')
                .map(s => s.trim())
                .filter(Boolean);

            const payload = {
                domain: formData.domain,
                role: formData.role,
                focusArea: formData.focusArea,
                itemHints: hintsArray,
                extraRules: formData.extraRules
            };

            if (formData.id) {
                await apiService.put(`api/gp/domains/${formData.id}`, payload);
                addToast("Domain updated successfully!", "success");
            } else {
                await apiService.post("api/gp/domains", payload);
                addToast("Domain created successfully!", "success");
            }

            setView("LIST");
            fetchDomains();
        } catch (error: any) {
            addToast(error?.response?.data?.error || "Failed to save domain.", "error");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <GenericModal
                isOpen={isOpen}
                onClose={onClose}
                header={view === "LIST" ? "Manage Domains" : (formData.id ? "Edit Domain" : "Create Domain")}
                customWidth="900px"
                disableContentScroll={view === "LIST"}
            >
                <div className="p-1 sm:p-1 max-h-[80vh] w-full">
                    {view === "LIST" ? (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center pb-2 border-gray-200 dark:border-gray-700">
                                <button onClick={handleAddDomainClick} className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-lg text-sm font-medium transition-colors">
                                    <Plus size={14} /> Add Domain
                                </button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-gray-400" size={24} /></div>
                            ) : domains.length === 0 ? (
                                <div className="text-center py-12 text-gray-500 text-sm bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                                    No domains found. Create your first one to get started.
                                </div>
                            ) : (
                                <div className="max-h-[55vh] overflow-y-auto pr-2">
                                    {domains.map(d => (
                                        <div key={d.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                            <div>
                                                <p className="font-semibold text-gray-900 dark:text-white text-base">{d.domain}</p>
                                                {d.role && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Role: {d.role}</p>}
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleEdit(d)} className="p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors shadow-sm"><Edit2 size={16} /></button>
                                                <button onClick={() => handleDelete(d.id)} className="p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors shadow-sm"><Trash2 size={16} /></button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={handleSave} className="space-y-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Domain Name <span className="text-red-500">*</span></label>
                                    <input type="text" value={formData.domain} onChange={e => setFormData({ ...formData, domain: e.target.value })} placeholder="e.g. BPO Customer Service" className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Target Role <span className="text-red-500">*</span></label>
                                    <input type="text" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} placeholder="e.g. Senior BPO trainer..." className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20" required />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Focus Area <span className="text-red-500">*</span></label>
                                <textarea rows={3} value={formData.focusArea} onChange={e => setFormData({ ...formData, focusArea: e.target.value })} placeholder="e.g. Focus ONLY on BPO customer service skills: call handling, active listening..." className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 resize-y" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Item Hints <span className="text-red-500">*</span> <span className="text-gray-400 font-normal ml-1">(One hint per line)</span></label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Press Enter for each new hint. The AI will use these directly to format questions.</p>
                                <textarea rows={6} value={formData.itemHints} onChange={e => setFormData({ ...formData, itemHints: e.target.value })} placeholder="True/False: A statement about correct call center behavior...&#10;MCQ: A scenario-based question about handling an angry customer..." className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 resize-y font-mono whitespace-pre leading-relaxed" wrap="off" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Extra Rules <span className="text-red-500">*</span></label>
                                <textarea rows={3} value={formData.extraRules} onChange={e => setFormData({ ...formData, extraRules: e.target.value })} placeholder="e.g. All MCQ scenarios must be realistic BPO call center situations. Avoid generic questions..." className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/20 resize-y" required />
                            </div>

                            <div className="flex justify-end gap-3 pt-1 mt-1 border-t border-gray-100 dark:border-gray-800">
                                <button type="button" onClick={() => setView("LIST")} className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 transition-colors">Back to List</button>
                                <button type="submit" disabled={saving || !isFormValid || !isFormDirty} className="flex items-center justify-center min-w-[140px] px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">
                                    {saving ? "Saving..." : 'Save Domain'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </GenericModal>
            <GenericModal
                isOpen={openDeleteModal}
                onClose={() => {
                    setOpenDeleteModal(false);
                    setSelectedDomainId(null);
                }}
                onAccept={handleConfirmDelete}
                onDecline={() => {
                    setOpenDeleteModal(false);
                    setSelectedDomainId(null);
                }}
                acceptText={deleting ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                header="Delete Domain"
                customWidth="500px"
            >
                <p className="text-base text-slate-600 dark:text-slate-300">
                    Do you want to delete this domain?
                </p>
            </GenericModal>
        </>
    );
}